import { UserOutlined } from "@ant-design/icons";
import { Checkbox, Form, Tag } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { $t } from "~/i18n.js";

/**
 * Computes the max length for a registration list, accounting for per_instructor type.
 */
export const getListMaxLength = (list, n_instructors) => {
  if (list.list_length_type === "per_instructor") {
    return (n_instructors || 0) * list.registration_list_length;
  }
  return list.registration_list_length;
};

/**
 * Returns true if the main list is full.
 */
export const isListFull = (list, n_instructors) => {
  const mainCount = list.registrations
    ? list.registrations.filter((r) => !r.is_waitlist).length
    : 0;
  return mainCount >= getListMaxLength(list, n_instructors);
};

/**
 * Given eligible lists (already filtered for group match), compute which list ids
 * should be pre-selected based on automatic_assignment and cluster logic:
 * - Lists without a cluster: each automatic_assignment list is independently selected.
 * - Lists with a cluster: only ONE automatic_assignment list per cluster is selected:
 *     → the first non-full one (sorted by order), or if all full, the first with has_waitinglist.
 */
const computeInitialSelected = (eligibleLists, n_instructors) => {
  const autoLists = eligibleLists.filter((l) => l.automatic_assignment);

  // Group by cluster
  const withoutCluster = autoLists.filter((l) => !l.cluster);
  const withCluster = autoLists.filter((l) => l.cluster);

  const selected = [];

  // No cluster — select all
  withoutCluster.forEach((l) => selected.push(l.id));

  // With cluster — one per cluster
  const clusters = [...new Set(withCluster.map((l) => l.cluster))];
  clusters.forEach((cluster) => {
    const clusterLists = withCluster
      .filter((l) => l.cluster === cluster)
      .sort((a, b) => a.order - b.order);

    // First non-full list
    const firstNonFull = clusterLists.find(
      (l) => !isListFull(l, n_instructors)
    );
    if (firstNonFull) {
      selected.push(firstNonFull.id);
      return;
    }

    // All full — pick first with waitinglist
    const firstWithWaitlist = clusterLists.find((l) => l.has_waitinglist);
    if (firstWithWaitlist) {
      selected.push(firstWithWaitlist.id);
    }
  });

  return selected;
};

export const AanmeldFormRegistrationList = ({
  registration_lists,
  profile,
  n_instructors,
  onChange,
}) => {
  // Filter to lists where user's groups overlap
  const eligibleLists = useMemo(() => {
    if (!registration_lists || !registration_lists.length) return [];
    return registration_lists.filter(
      (list) =>
        !list.groups ||
        list.groups.length === 0 ||
        list.groups.some((g) => profile.group_keys?.includes(g))
    );
  }, [registration_lists, profile.group_keys]);

  const [selectedIds, setSelectedIds] = useState(() =>
    computeInitialSelected(eligibleLists, n_instructors)
  );

  // Re-compute when eligible lists or n_instructors changes (e.g. modal re-opens)
  useEffect(() => {
    const initial = computeInitialSelected(eligibleLists, n_instructors);
    setSelectedIds(initial);
    onChange(initial);
  }, [eligibleLists, n_instructors]);

  if (!eligibleLists.length) return null;

  const handleChange = (listId, checked) => {
    const newIds = checked
      ? [...selectedIds, listId]
      : selectedIds.filter((id) => id !== listId);
    setSelectedIds(newIds);
    onChange(newIds);
  };

  // Build a set of clusters already covered by a checked list
  const checkedClusters = new Set(
    eligibleLists
      .filter((l) => l.cluster && selectedIds.includes(l.id))
      .map((l) => l.cluster)
  );

  return (
    <Form.Item label={$t("Inschrijvingen")}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {eligibleLists
          .slice()
          .sort((a, b) => a.order - b.order)
          .map((list) => {
            const full = isListFull(list, n_instructors);
            const isChecked = selectedIds.includes(list.id);

            // Disable if another list in the same cluster is already checked
            const clusterTaken =
              list.cluster &&
              checkedClusters.has(list.cluster) &&
              !isChecked;

            const mainCount = list.registrations
              ? list.registrations.filter((r) => !r.is_waitlist).length
              : 0;
            const maxLength = getListMaxLength(list, n_instructors);

            return (
              <div key={list.id} className="row" style={{ gap: 6 }}>
                <Checkbox
                  checked={isChecked}
                  disabled={clusterTaken || (!list.has_waitinglist && full)}
                  onChange={(e) => handleChange(list.id, e.target.checked)}
                >
                  {list.name}
                </Checkbox>
                <Tag
                  style={{ fontSize: 11, padding: 2, lineHeight: 1.1 }}
                  color={full ? "red" : "green"}
                >
                  <UserOutlined /> {mainCount} / {maxLength}
                </Tag>
                {full && list.has_waitinglist && (
                  <Tag style={{ fontSize: 11, padding: 2, lineHeight: 1.1 }}>
                    {$t("Wachtlijst")}
                  </Tag>
                )}
              </div>
            );
          })}
      </div>
    </Form.Item>
  );
};
